import {
  ArrowLeftRight,
  Briefcase,
  Calendar,
  Clock,
  GraduationCap,
  IndianRupee,
  Route as RouteIcon,
  ShieldCheck,
  Sparkles,
  Sun,
} from "lucide-react";
import type { CorridorDefinition } from "@/types/corridor";

const useCaseIcons = [Sun, Briefcase, Calendar, GraduationCap] as const;

type Props = {
  corridor: CorridorDefinition;
};

export function CorridorRouteGuideSection({ corridor }: Props) {
  const guide = corridor.routeGuide;
  if (!guide) return null;

  const { stats, cityA, cityB } = corridor;

  return (
    <>
      <section className="bg-muted/40 py-10 sm:py-12 lg:py-14">
        <div className="container">
          <h2 className="text-2xl font-bold text-foreground md:text-3xl">
            {cityA} ↔ {cityB} — route overview
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">{guide.overview}</p>

          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <RouteIcon className="h-5 w-5 text-primary" /> Distance & drive time
              </h3>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li>
                  <strong className="text-foreground">Distance:</strong> {stats.distanceKm} km via {stats.highway}
                </li>
                <li>
                  <strong className="text-foreground">Drive time:</strong> {stats.driveTime}
                </li>
                {guide.typicalBreak ? (
                  <li>
                    <strong className="text-foreground">Typical break:</strong> {guide.typicalBreak}
                  </li>
                ) : null}
                {guide.tollsNote ? (
                  <li>
                    <strong className="text-foreground">Tolls:</strong> {guide.tollsNote}
                  </li>
                ) : null}
              </ul>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <ArrowLeftRight className="h-5 w-5 text-primary" /> Pickup & drop zones
              </h3>
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase text-muted-foreground">{cityA} stops</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {stats.zonesA.map((z) => (
                    <span key={z} className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                      {z}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-xs font-semibold uppercase text-muted-foreground">{cityB} stops</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {stats.zonesB.map((z) => (
                    <span key={z} className="rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent">
                      {z}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <IndianRupee className="h-5 w-5 text-primary" /> Cost comparison
              </h3>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Private cab</span>
                  <span className="font-medium text-foreground">~₹{stats.cabFare.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>AC bus</span>
                  <span className="font-medium text-foreground">~₹{stats.busFare}</span>
                </div>
                <div className="flex justify-between rounded-lg bg-primary/10 px-3 py-2 font-semibold text-primary">
                  <span>Duri carpool</span>
                  <span>from ₹{stats.carpoolFare}</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">Per person, one way. Live fares in-app.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="container py-10 sm:py-12 lg:py-14">
        <h2 className="text-2xl font-bold text-foreground md:text-3xl">Who travels this route — and why</h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Real use cases on {cityA}–{cityB}, not generic carpool copy.
        </p>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {guide.useCases.map((uc, i) => {
            const Icon = useCaseIcons[i % useCaseIcons.length];
            return (
              <article key={uc.title} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{uc.title}</h3>
                    <p className="mt-0.5 text-xs font-medium uppercase tracking-wide text-primary">{uc.audience}</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{uc.description}</p>
                    {uc.tip ? (
                      <p className="mt-3 rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                        <strong className="text-foreground">Tip:</strong> {uc.tip}
                      </p>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="bg-muted/40 py-10 sm:py-12 lg:py-14">
        <div className="container">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">When rides are usually available</h2>
          </div>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Expected windows based on how hosts actually drive {cityA}–{cityB}. Times shift slightly on holidays.
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {guide.availabilityWindows.map((w) => (
              <div key={w.label} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-semibold text-foreground">{w.label}</h3>
                <p className="mt-2 text-lg font-bold text-primary">{w.times}</p>
                <p className="mt-2 text-sm text-muted-foreground">{w.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container py-10 sm:py-12 lg:py-14">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="text-2xl font-bold text-foreground md:text-3xl">{guide.whyDuri.title}</h2>
        </div>
        <ul className="mx-auto mt-8 max-w-3xl space-y-4">
          {guide.whyDuri.points.map((point) => (
            <li key={point} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-muted/40 py-10 sm:py-12 lg:py-14">
        <div className="container">
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="text-center text-2xl font-bold text-foreground md:text-3xl">
              Trust & safety on {cityA}–{cityB}
            </h2>
          </div>
          <div className="mx-auto mt-10 grid max-w-5xl gap-5 sm:grid-cols-2">
            {guide.trustSteps.map((t) => (
              <div key={t.step} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-semibold text-foreground">{t.step}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
